import { useState } from 'react';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactInfo = () => {
  const contactDetails = [
    { 
      icon: Mail, 
      label: 'Email', 
      value: 'hello@theroaslabs.com',
      href: 'mailto:hello@theroaslabs.com'
    },
    { 
      icon: Phone, 
      label: 'Phone', 
      value: '+91-9966650720',
      href: 'tel:+919966650720'
    },
    { 
      icon: MapPin, 
      label: 'Location', 
      value: 'India | SEA | Global Remote',
      href: null
    },
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
          Let's Build Your <span className="text-neon-green">Growth Engine.</span>
        </h1>
        <p className="text-xl text-gray-400 leading-relaxed">
          Whether you're scaling a gaming studio or launching the next breakout app — we're ready.
        </p>
      </div>
      
      <div className="space-y-4">
        {contactDetails.map((item, index) => (
          <div 
            key={index}
            className="flex items-center gap-4"
            data-testid={`contact-info-${item.label.toLowerCase()}`}
          >
            <div className="w-12 h-12 rounded-lg bg-neon-green/10 flex items-center justify-center flex-shrink-0">
              <item.icon className="w-6 h-6 text-neon-green" />
            </div>
            <div>
              <p className="text-gray-500 text-sm">{item.label}</p>
              {item.href ? (
                <a 
                  href={item.href}
                  className="text-white font-medium hover:text-neon-green transition-colors"
                >
                  {item.value}
                </a>
              ) : (
                <p className="text-white font-medium">{item.value}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service_interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const serviceOptions = [
    'Display Advertising',
    'Mobile Marketing',
    'Lead Generation',
    'Creative Solutions',
    'Social Media Marketing',
    'Full-Service Partnership'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service_interest: '',
        message: ''
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="glass rounded-xl p-8 text-center" data-testid="contact-success">
        <div className="w-16 h-16 bg-neon-green/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-8 h-8 text-neon-green" />
        </div>
        <h3 className="font-heading text-2xl font-semibold text-white mb-4">
          Thank You!
        </h3>
        <p className="text-gray-400 mb-6">
          We've received your message and will get back to you within 24 hours.
        </p>
        <button
          onClick={() => setIsSubmitted(false)}
          className="btn-secondary"
          data-testid="contact-send-another"
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="glass rounded-xl p-8" data-testid="contact-form">
      <h2 className="font-heading text-2xl font-semibold text-white mb-6">
        Book a Free Growth Audit
      </h2>
      
      <div className="space-y-5">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2">
            Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full bg-black/50 border border-white/10 focus:border-neon-green/50 text-white rounded-md h-12 px-4 focus:outline-none focus:ring-1 focus:ring-neon-green/50 transition-colors"
            placeholder="Your name"
            data-testid="contact-name-input"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full bg-black/50 border border-white/10 focus:border-neon-green/50 text-white rounded-md h-12 px-4 focus:outline-none focus:ring-1 focus:ring-neon-green/50 transition-colors"
            placeholder="your@email.com"
            data-testid="contact-email-input"
          />
        </div>

        {/* Company & Phone Row */}
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-400 mb-2">
              Company
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full bg-black/50 border border-white/10 focus:border-neon-green/50 text-white rounded-md h-12 px-4 focus:outline-none focus:ring-1 focus:ring-neon-green/50 transition-colors"
              placeholder="Company name"
              data-testid="contact-company-input"
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-400 mb-2">
              Phone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full bg-black/50 border border-white/10 focus:border-neon-green/50 text-white rounded-md h-12 px-4 focus:outline-none focus:ring-1 focus:ring-neon-green/50 transition-colors"
              placeholder="+1 234 567 8900"
              data-testid="contact-phone-input"
            />
          </div>
        </div>

        {/* Service Interest */}
        <div>
          <label htmlFor="service_interest" className="block text-sm font-medium text-gray-400 mb-2">
            Service Interest
          </label>
          <select
            id="service_interest"
            name="service_interest"
            value={formData.service_interest}
            onChange={handleChange}
            className="w-full bg-black/50 border border-white/10 focus:border-neon-green/50 text-white rounded-md h-12 px-4 focus:outline-none focus:ring-1 focus:ring-neon-green/50 transition-colors appearance-none cursor-pointer"
            data-testid="contact-service-select"
          >
            <option value="" className="bg-surface">Select a service</option>
            {serviceOptions.map((option, index) => (
              <option key={index} value={option} className="bg-surface">
                {option}
              </option>
            ))}
          </select>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-2">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={4}
            className="w-full bg-black/50 border border-white/10 focus:border-neon-green/50 text-white rounded-md p-4 focus:outline-none focus:ring-1 focus:ring-neon-green/50 transition-colors resize-none"
            placeholder="Tell us about your project and goals..."
            data-testid="contact-message-input"
          />
        </div>

        {/* Error Message */}
        {error && (
          <p className="text-red-400 text-sm" data-testid="contact-error">
            {error}
          </p>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="contact-submit-btn"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Send Message
            </>
          )}
        </button>
      </div>
    </form>
  );
};

const Contact = () => {
  return (
    <div className="min-h-screen grid-bg py-20 md:py-32" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <ContactInfo />
          <ContactForm />
        </div>
      </div>
    </div>
  );
};

export default Contact;
